// ═══════════════════════════════════════════════════════════════════════════
// AI safe-contour mount (CC-2b — S-AIDRAFT-WIRE)
// ═══════════════════════════════════════════════════════════════════════════
//
// This is the app-side entry that mounts `@dxf-ai-editor/ai-commands` behind
// the editor's EXISTING safe contour. It takes an AI-proposed command (or a
// batch), runs it through the package's domain-agnostic translator
// (`translateAiCommandBatch`, which calls the CC-2a fail-closed
// `assertAiCallableAction` guard), and routes the resulting `EditorCommand`s
// through the SAME `store.dispatch` the UI / command line use — so validation,
// the audit trail, undo history, and shell-resync all apply to AI operations
// without any parallel write path.
//
// Pipeline (the rule "AI ... может только предлагать и исполнять команды через
// безопасный контур" — CLAUDE.md):
//
//   AI command(s)
//     │
//     ├─ 1. SCHEMA PARSE (fail-closed) ─ parseAiCommandBatch runs every
//     │      proposal through the combined discriminated `aiCommandSchema`, so
//     │      schema defaults are MATERIALISED, refines ENFORCED, and a
//     │      malformed / degenerate / unknown-action proposal is REJECTED
//     │      (AiCommandValidationError) BEFORE any guard, translation, batch or
//     │      dispatch. Without this the per-schema .default()/.refine() rules
//     │      are dead and a bad proposal throws later at the contract parse —
//     │      after a history batch is already open.
//     │
//     ├─ 2. CC-2a aiCallable GUARD ─ translateAiCommandBatch runs
//     │      assertAiCallableAction (CC-2a) for EVERY action BEFORE any
//     │      EditorCommand is dispatched. A non-aiCallable / unknown target
//     │      throws NonAiCallableCommandError here — so no history batch is ever
//     │      opened for a rejected proposal (fail-closed at the app boundary).
//     │
//     ├─ 3. GROUPED UNDO ─ wrap the translated sequence in ONE history batch
//     │      (history/begin-batch … history/commit-batch) so a single undo
//     │      reverses the entire AI turn. On a mid-turn dispatch THROW the batch
//     │      is ABORTED (history/abort-batch — rolled back), never committed.
//     │
//     └─ 4. DISPATCH → AUDIT → HISTORY ─ each EditorCommand flows through the
//            real store.dispatch: schema validation, pushAuditEvent, undo
//            recording. AI never writes entities directly. The
//            LAST_INSERT_ID_SENTINEL placeholder on INSERT_BLOCK attribute
//            updates is resolved to the freshly-created INSERT id here.
//
// T6b (AI-dispatch coalescing) — step 4 additionally COALESCES a maximal run
// of ≥2 consecutive `geometry/create-block-insert` commands that share the
// same spaceId/blockName/color (an AI MINSERT's row×column fan-out, produced
// by `translateInsertBlock`) into ONE `geometry/create-block-inserts-batch`
// dispatch instead of K individual dispatches. This fixes an O(N·K) freeze on
// a large MINSERT (each individual create-block-insert dispatch pays its own
// O(N) `nextGeometryEntityId` scan + O(N) `assembleDrawingDocument` validate,
// and the `LAST_INSERT_ID_SENTINEL` resolution read the O(N) insert-id set
// TWICE per create) down to O(N) total — mirroring T6a's in-reducer batch-
// append pattern (`createEntityIdAllocator` + `appendGeometryEntities`),
// applied here to the AI bridge-dispatch path. A run of length 1 (the
// ubiquitous non-array INSERT) is left as a plain single dispatch — zero
// behavioural change for that path. See `dispatchAiCommandSequence`'s own
// JSDoc for the full grouping + id-bookkeeping contract.
//
// DEPENDENCY DIRECTION (deps-graph): this module imports ONLY from
// `@dxf-ai-editor/ai-commands` (+ contracts / command-registry types). It takes
// `dispatch` as a parameter rather than importing the store, so there is no
// `editorStore → aiSafeContour → editorStore` cycle. The store wires it up by
// passing `useEditorStore.getState().dispatch` (see editorStore.ts).
//
// CC-2b is the MOUNT (authoring) only — it adds NO UI element. The live,
// canvas-mutating execution surface + AI drafting panel are CC-2c / CC-2d.

import type { EditorCommand } from "@dxf-ai-editor/contracts";
import type { CommandDescriptor } from "@dxf-ai-editor/command-registry";
import {
  AI_COMMAND_SOURCE,
  AI_INSERT_GROUP_SIZE_HINT,
  LAST_INSERT_ID_SENTINEL,
  parseAiCommandBatch,
  translateAiCommandBatch,
  type AiCommand,
  type AiDispatchContext,
} from "@dxf-ai-editor/ai-commands";

/**
 * A dispatch sink — exactly the shape of `useEditorStore`'s `dispatch`. Kept
 * as a parameter (dependency injection) so the contour stays decoupled from
 * the store singleton and is trivially testable with a spy.
 */
export type AiSafeContourDispatch = (command: EditorCommand) => void;

/** Options for routing AI commands through the safe contour. */
export interface RunAiThroughSafeContourOptions {
  /** Runtime context the translator needs (active space, name resolvers). */
  context?: AiDispatchContext;
  /**
   * Registry descriptors used by the CC-2a guard. Injectable for tests /
   * alternate registries; defaults (inside the package) to the real registry.
   */
  descriptors?: readonly CommandDescriptor[];
  /**
   * Grouped-undo batch label (surfaces in the Undo tooltip). User-facing copy
   * is owned by the CC-2d panel via `useT`; the mount uses a neutral,
   * language-agnostic token by default so CC-2b adds no localizable string.
   */
  label?: string;
  /**
   * Reads the ids of every INSERT (Block) entity currently in the document, in
   * document order. Injected (DI, like `dispatch`) so the contour can resolve
   * the `LAST_INSERT_ID_SENTINEL` placeholder emitted by INSERT_BLOCK-with-
   * attributes: after dispatching `geometry/create-block-insert` the contour
   * diffs this set to find the freshly created id and substitutes it into the
   * trailing `geometry/update-attribute-values` command. When absent (e.g.
   * dispatch-spy tests) the sentinel command is DROPPED rather than dispatched
   * as a guaranteed no-op against the placeholder id.
   */
  resolveInsertIds?: () => readonly string[];
  /**
   * Snapshot EVERY entity currently in the document as `{ id, fingerprint }`
   * rows, in document order. Injected (DI, like `dispatch` / `resolveInsertIds`)
   * so the contour can surface the ids of the entities its dispatched commands
   * CREATED (`createdEntityIds`) and the existing entities a modifier TOUCHED
   * (`affectedEntityIds`) on {@link AiSafeContourResult} — the agent-editing
   * (T2) create→read→erase loop needs the ids it just made to address them.
   *
   * The `fingerprint` is a stable per-entity serialization (`JSON.stringify(entity)`),
   * mirroring the canonical `diffDocumentMutations` `entitiesShapeEqual`
   * (`JSON.stringify(a) === JSON.stringify(b)`) so the contour's create-vs-affect
   * classification is IDENTICAL to the store's own `EntityMutation` diff:
   *   • CREATED = id present AFTER the turn but not BEFORE (mirrors `added`).
   *   • AFFECTED = id present in BOTH but with a CHANGED fingerprint (mirrors
   *     `updated`) — an in-place MOVE/ROTATE/SCALE keeps the SAME id, so an id-set
   *     diff alone cannot see it; the fingerprint compare is load-bearing here.
   *   • REMOVED = id present in BEFORE but gone AFTER (mirrors `removed`) — T3
   *     surfaces these as `removedEntityIds` so DELETE_ENTITIES (ERASE) reports what
   *     it actually deleted (a block-def `attdef-*` id, which the space-only reducer
   *     cannot delete, is EXCLUDED → the no-op is NON-SILENT to the agent).
   *
   * READ-ONLY surfacing: this is a snapshot READER (no write path). The ids come
   * from the command-engine mutations the contour ALREADY dispatched — it is NOT a
   * re-dispatch. A SINGLE before/after snapshot bracketing the whole turn yields the
   * per-turn net diff: an entity created then modified WITHIN the same turn is net-NEW
   * (CREATED only, never double-counted as affected). When absent (e.g. dispatch-spy
   * tests with no document) both id arrays are empty.
   */
  snapshotEntities?: () => readonly AiEntitySnapshotEntry[];
}

/**
 * One row of the {@link RunAiThroughSafeContourOptions.snapshotEntities} reader: an
 * entity id paired with a stable content fingerprint (`JSON.stringify(entity)`). The
 * contour diffs the BEFORE/AFTER snapshots by id + fingerprint to classify each
 * entity as created (new id) / affected (same id, changed fingerprint) — the SAME
 * keys the store's `diffDocumentMutations` uses (`added` vs `updated`).
 */
export interface AiEntitySnapshotEntry {
  readonly id: string;
  readonly fingerprint: string;
}

/** Outcome of a safe-contour run. */
export interface AiSafeContourResult {
  /** The EditorCommands the translator produced (already dispatched). */
  commands: EditorCommand[];
  /**
   * Whether the dispatch was wrapped in a grouped-undo history batch. `false`
   * only when the translation produced zero commands (nothing to dispatch).
   */
  grouped: boolean;
  /**
   * The CANONICAL PARSED batch (`parseAiCommandBatch(proposed)`) — the proposal
   * AFTER Zod `.default()` materialization (`bulge:0`, `closed:false`,
   * `startWidth:0`, …) and `.refine()` enforcement. This is the AUTHORITATIVE
   * identity over which `command.run`'s browser-authoritative `batchHash` is
   * computed (ABB-S4 / INV-HASH): two logically-identical turns that differ ONLY
   * in an omitted-vs-explicit defaulted field hash IDENTICALLY (the raw forwarded
   * batch would NOT — defeating the companion's replay/de-dup, `correlation.ts`).
   * Behaviour-neutral: the contour already computes `parsed` for translation; this
   * merely surfaces it on the result so the hash binds to the parsed canonical form.
   */
  parsed: AiCommand[];
  /**
   * The ids of the entities this turn's dispatched commands CREATED — the
   * `DrawingEntity.id`s present in the document AFTER the turn but not BEFORE
   * (mirrors `EntityMutation.added`). CREATE / COPY / MIRROR / ARRAY / OFFSET land
   * here. These are the SAME stable ids the T1 read-API projects and T3 erase
   * targets (the cross-task stable-ID identity), so the agent can address what it
   * just made. Deduped, in document order. Empty when no `snapshotEntities` reader
   * is wired (the contour cannot see the document) or nothing was created.
   */
  createdEntityIds: string[];
  /**
   * The ids of the EXISTING entities this turn's dispatched commands TOUCHED — the
   * `DrawingEntity.id`s present in the document BOTH before and after the turn whose
   * content CHANGED (mirrors `EntityMutation.updated`). MOVE / ROTATE / SCALE /
   * STRETCH / TRIM / EXTEND land here (the entity is modified in place, NOT created).
   * Deduped, in document order. Empty when no `snapshotEntities` reader is wired or
   * nothing existing was modified.
   */
  affectedEntityIds: string[];
  /**
   * T3 (agent-editing) — the ids of the entities this turn's dispatched commands
   * REMOVED: the `DrawingEntity.id`s present in the document BEFORE the turn but gone
   * AFTER (mirrors `EntityMutation.removed`). DELETE_ENTITIES (ERASE) of a SPACE entity
   * lands here. Deduped, in document (BEFORE) order. Empty when no `snapshotEntities`
   * reader is wired or nothing was removed.
   *
   * HONEST-CONTRACT (T3 P2): `DELETE_ENTITIES` → `shell/delete-entities` deletes ONLY
   * SPACE entities (`document.spaces[].entities`). A block-definition / ATTDEF id (e.g.
   * an `attdef-*` from `CREATE_ATTDEF`, which `collectAiEntitySnapshot` surfaces from
   * `document.blocks[].entities`) is NOT deletable through this path — it survives the
   * turn, so it is EXCLUDED from `removedEntityIds`. The agent can therefore tell a
   * requested delete of such an id did NOT take effect (the no-op is NON-SILENT) instead
   * of the advertised create→delete loop failing with no signal. `removedEntityIds` is
   * thus the AUTHORITATIVE record of what DELETE_ENTITIES actually removed.
   */
  removedEntityIds: string[];
}

/**
 * Neutral undo-group label. "AI" is identical across RU/EN, so the mount adds
 * no user-facing string that would need i18n (CC-2d supplies a localized
 * label when it owns the visible surface).
 */
const DEFAULT_AI_UNDO_LABEL = "AI";

/** Read the INSERT_BLOCK group-size hint off a sentinel attribute command. */
function readInsertGroupSize(command: EditorCommand): number {
  const raw = (command as Record<string, unknown>)[AI_INSERT_GROUP_SIZE_HINT];
  return typeof raw === "number" && raw >= 1 ? raw : 1;
}

/** A `geometry/create-block-insert` EditorCommand, narrowed for the grouping pass. */
type CreateBlockInsertCommand = Extract<
  EditorCommand,
  { type: "geometry/create-block-insert" }
>;

/**
 * Whether two `create-block-insert` commands belong to the SAME coalesced
 * batch: identical `spaceId` / `blockName` / `color`. `color` is compared
 * structurally (`JSON.stringify`) — both sides are small typed unions
 * (`EntityColor | undefined`), so this is cheap and exact. `scaleX` /
 * `scaleY` / `rotation` / `insertionPoint` are deliberately NOT compared —
 * those are the PER-COPY fields the batch command carries in `inserts[]`.
 */
function isSameBlockInsertGroup(
  a: CreateBlockInsertCommand,
  b: CreateBlockInsertCommand,
): boolean {
  return (
    a.spaceId === b.spaceId &&
    a.blockName === b.blockName &&
    JSON.stringify(a.color) === JSON.stringify(b.color)
  );
}

/**
 * T6b dispatch unit — the output of the grouping pass ({@link groupIntoDispatchUnits}).
 * A `batch` unit replaces `originalCount` consecutive `create-block-insert`
 * commands with ONE `geometry/create-block-inserts-batch` dispatch;
 * `originalCount` is preserved so the id-bookkeeping pass below can still push
 * exactly one `createdSlots` entry per ORIGINAL create (the group-size sentinel
 * math downstream is entirely oblivious to whether a slot came from a batch or
 * a single dispatch).
 */
type AiDispatchUnit =
  | { kind: "single"; command: EditorCommand }
  | { kind: "batch"; originalCount: number; batchCommand: EditorCommand };

/**
 * B1 — the coalescing/grouping pass. Scans `commands` for maximal RUNS
 * (length ≥ 2) of consecutive `geometry/create-block-insert` commands sharing
 * the same spaceId/blockName/color and replaces each run with ONE `batch`
 * unit; every other command (including a lone/non-run `create-block-insert`,
 * and every non-insert command such as the trailing attribute sentinel)
 * becomes its own `single` unit, UNCHANGED, preserving relative order.
 *
 * A run of length 1 is intentionally left as a `single` unit — the ubiquitous
 * non-array INSERT keeps dispatching the plain `geometry/create-block-insert`
 * exactly as before T6b, zero behavioural change for that path.
 *
 * The synthesized batch command's per-batch fields (`spaceId`/`blockName`/
 * `color`/`source`) are taken from the run's FIRST command (all members are
 * identical by construction — that is exactly the grouping predicate); its
 * `inserts[]` carries every run member's per-copy fields in original order.
 */
function groupIntoDispatchUnits(
  commands: readonly EditorCommand[],
): AiDispatchUnit[] {
  const units: AiDispatchUnit[] = [];
  let i = 0;
  while (i < commands.length) {
    const command = commands[i];
    if (command.type !== "geometry/create-block-insert") {
      units.push({ kind: "single", command });
      i += 1;
      continue;
    }
    let j = i + 1;
    while (
      j < commands.length &&
      commands[j].type === "geometry/create-block-insert" &&
      isSameBlockInsertGroup(
        command,
        commands[j] as CreateBlockInsertCommand,
      )
    ) {
      j += 1;
    }
    const run = commands.slice(i, j) as CreateBlockInsertCommand[];
    if (run.length >= 2) {
      units.push({
        kind: "batch",
        originalCount: run.length,
        batchCommand: {
          type: "geometry/create-block-inserts-batch",
          source: run[0].source,
          spaceId: run[0].spaceId,
          blockName: run[0].blockName,
          color: run[0].color,
          inserts: run.map((c) => ({
            insertionPoint: c.insertionPoint,
            scaleX: c.scaleX,
            scaleY: c.scaleY,
            rotation: c.rotation,
          })),
        } as EditorCommand,
      });
    } else {
      units.push({ kind: "single", command: run[0] });
    }
    i = j;
  }
  return units;
}

/**
 * Dispatch the translated EditorCommand sequence, resolving the
 * `LAST_INSERT_ID_SENTINEL` placeholder along the way.
 *
 * INSERT_BLOCK-with-attributes translates to one or more
 * `geometry/create-block-insert` commands (a MINSERT fans out to row×col copies,
 * row0/col0 FIRST) followed by a single `geometry/update-attribute-values` whose
 * `insertId` is the sentinel (the concrete id is unknown at translation time).
 *
 * T6b (AI-dispatch coalescing) — BEFORE dispatching, {@link groupIntoDispatchUnits}
 * (B1) replaces every maximal run of ≥2 consecutive `create-block-insert`
 * commands sharing the same spaceId/blockName/color with ONE
 * `geometry/create-block-inserts-batch` dispatch unit, so the reducer pays ONE
 * `assembleDrawingDocument` pass for the whole run instead of K. The dispatch +
 * id-bookkeeping pass below (B2) then iterates UNITS, not raw commands.
 *
 * We diff the INSERT-id set (via `resolveInsertIds`) after EACH creating unit
 * (single create OR batch) to learn the freshly minted id(s) and record them,
 * in order, into `createdSlots` — ONE slot per ORIGINAL create command (a batch
 * unit contributes `originalCount` slots at once), holding the minted id OR
 * `undefined` when that slot's create changed nothing (it targeted a
 * non-existent space, so the append was a no-op — all-or-nothing for a whole
 * batch, since every copy in one batch shares the SAME spaceId). Unlike the
 * pre-T6b per-create loop (which re-read `resolveInsertIds` fresh immediately
 * before EVERY create), the id-bookkeeping here maintains ONE lazily-initialized
 * running set (`knownInsertIds`), refreshed after EVERY dispatched unit — not
 * only creating ones — once tracking has started (see the STALENESS FIX note
 * below). Each creating unit's own dispatch still reads `resolveInsertIds()`
 * exactly ONCE for its OWN diff (the "after" read doubles as the running
 * baseline), so the read count stays O(number of units in the turn) — the
 * batching win is unaffected: a big MINSERT is still ONE unit either way, and
 * only OTHER interleaved commands in the same AI turn add extra reads, which is
 * never large.
 *
 * STALENESS FIX (P2, independent local Codex review): a unit that is NOT
 * itself a tracked creating unit — the sentinel attribute-update dispatch, and
 * the generic fallthrough `dispatch(command)` at the end of the loop — can
 * ALSO mint a `Block` (INSERT) entity as a side effect, e.g. a COPY
 * (`geometry/copy`) targeting a pre-existing Block insert. Before this fix
 * `knownInsertIds` was only refreshed by creating units, so a sequence like
 * [attributed INSERT_BLOCK A, COPY(existing Block insert), attributed
 * INSERT_BLOCK B] left the running baseline STALE across the COPY: block B's
 * `mintedIds` diff then included BOTH the COPY's un-subtracted clone id AND
 * B's own genuinely-new id, the count no longer matched the expected
 * `groupSize`/`originalCount`, and the existing fail-closed branch silently
 * DROPPED block B's attribute sentinel even though its create had actually
 * succeeded. The fix: refresh `knownInsertIds` after ANY dispatched unit
 * (once tracking has started), not only creating ones, so the running
 * baseline can never go stale relative to an intervening command's side
 * effects.
 *
 * The sentinel carries an internal `AI_INSERT_GROUP_SIZE_HINT` = how many
 * ORIGINAL creates belong to ITS block (rowCount×columnCount); the FIRST insert
 * of that block is therefore `createdSlots[createdSlots.length - groupSize]`.
 * Substituting THAT id into the trailing attribute update lands the AI-supplied
 * values on the FIRST INSERT (the AutoCAD MINSERT primary instance, the
 * contracted target). This math is entirely UNCHANGED by coalescing — it
 * operates purely on the per-original-create `createdSlots` array, which stays
 * byte-identical in length/order/content to the pre-T6b per-create loop
 * regardless of whether a group of ≥2 was coalesced into one batch dispatch or
 * not (a coalesced batch's `mintedIds` are extracted in `command.inserts` order
 * — the SAME order the reducer's `createEntityIdAllocator` mints them in, which
 * is the SAME order a per-create loop would have minted them in).
 *
 * Using the group-size boundary — instead of "first of a consecutive create
 * run" — fixes the adjacent-block leak (#283-1): an attr-free INSERT_BLOCK
 * immediately followed by an attributed one produces two ADJACENT creates with
 * one trailing sentinel; the run-based code bound the sentinel to the FIRST
 * block's insert. Indexing back `groupSize` from the per-create slot array
 * instead binds each block's attrs to its OWN first insert regardless of
 * interleaving — this ALSO covers the case where coalescing merges two
 * adjacent INSERT_BLOCK groups (sharing spaceId/blockName/color) into ONE
 * batch dispatch unit: the slot array is still populated in original per-copy
 * order, so the backward `groupSize` index still lands on the correct group's
 * first insert regardless of how many dispatches produced the slots.
 *
 * The hint is STRIPPED before dispatch so it never reaches the reducer/contract.
 *
 * Keeping a slot for EVERY create (not just successful ones) is load-bearing: it
 * preserves the create↔id mapping so a 0-mint create does NOT shift the
 * group-size index onto an EARLIER block's insert. When a group minted FEWER ids
 * than expected (zero, or a partial run because it targeted a non-existent
 * space) the sentinel is DROPPED — fail-closed — rather than mis-bound to a
 * stale earlier insert (a cumulative success-only buffer would otherwise
 * overwrite a DIFFERENT block's attributes when the tags match).
 *
 * The diff is only performed when the sequence actually contains a sentinel
 * attribute command AND a resolver is available, so the common attribute-free
 * INSERT path pays no extra reads.
 *
 * T2 (agent-editing): when a `snapshotEntities` reader is wired, the sequence is
 * bracketed with ONE before/after entity snapshot; the per-turn net diff (by id +
 * fingerprint) classifies each entity as CREATED (new id) or AFFECTED (same id,
 * changed fingerprint) — the SAME `added`/`updated` semantics as the store's
 * `diffDocumentMutations`. The result is returned so the contour surfaces
 * `createdEntityIds` / `affectedEntityIds` on {@link AiSafeContourResult}. This is a
 * READ-ONLY surfacing of the mutations the dispatched commands ALREADY produced — no
 * extra dispatch, no write path.
 */
function dispatchAiCommandSequence(
  commands: readonly EditorCommand[],
  dispatch: AiSafeContourDispatch,
  resolveInsertIds: (() => readonly string[]) | undefined,
  snapshotEntities: (() => readonly AiEntitySnapshotEntry[]) | undefined,
): {
  createdEntityIds: string[];
  affectedEntityIds: string[];
  removedEntityIds: string[];
} {
  // T2/T3 — BEFORE-snapshot the whole document (id → fingerprint) so the AFTER
  // diff below classifies this turn's net mutations. Skipped (empty result) when
  // no reader is wired — the common dispatch-spy / no-document path pays nothing.
  // The BEFORE rows are kept in document ORDER (`beforeOrdered`) so the REMOVED
  // diff (T3: present BEFORE, gone AFTER) is surfaced in document order, mirroring
  // the AFTER-ordered created/affected arrays — the SAME single snapshot already
  // taken, no second document scan.
  const beforeOrdered =
    snapshotEntities !== undefined ? snapshotEntities() : undefined;
  const beforeById =
    beforeOrdered !== undefined
      ? new Map(beforeOrdered.map((e) => [e.id, e.fingerprint]))
      : undefined;

  const needsInsertIdResolution =
    resolveInsertIds !== undefined &&
    commands.some(
      (command) =>
        command.type === "geometry/update-attribute-values" &&
        command.insertId === LAST_INSERT_ID_SENTINEL,
    );

  // T6b B1 — coalesce maximal runs of ≥2 same-group creates into batch units.
  const units = groupIntoDispatchUnits(commands);

  // One slot per ORIGINAL `geometry/create-block-insert`, in order — the id it
  // minted, or `undefined` when that create changed nothing (it targeted a
  // non-existent space, so the append was a no-op). A trailing sentinel targets
  // the FIRST insert of ITS block group = the slot `groupSize` back from the
  // end (each INSERT_BLOCK emits its creates then its sentinel contiguously).
  // Keeping a slot for EVERY create — successful or not — keeps this index
  // aligned to THIS block's run. Populated only when a sentinel needs
  // resolution.
  const createdSlots: Array<string | undefined> = [];
  // T6b B2 — lazily-initialized running id set carried across ALL dispatched
  // units, not only creating ones, once tracking has started — refreshed
  // after every unit's dispatch so an intervening NON-creating command (e.g.
  // a COPY that clones a pre-existing Block insert as a side effect) can
  // never leave the baseline stale for a LATER creating unit's diff (P2 fix,
  // independent local Codex review — see the STALENESS FIX paragraph above).
  // The "after" read of one unit doubles as the NEXT unit's "before"
  // baseline, so `resolveInsertIds()` is called ONCE per dispatched unit once
  // tracking has started (plus one extra initial read to seed it).
  let knownInsertIds: Set<string> | undefined;

  // T6b staleness fix — call after dispatching a NON-creating unit (the
  // sentinel attribute update, or the generic fallthrough) to fold that
  // command's side effects (if any) into the running baseline BEFORE the next
  // creating unit's diff runs. A no-op when tracking is inactive or has not
  // started yet — the first creating unit's own lazy `resolveInsertIds()` seed
  // already captures everything that happened before it, so there is nothing
  // to gain re-reading for a command that runs before tracking starts.
  const refreshKnownInsertIdsAfterNonCreatingDispatch = (): void => {
    if (
      needsInsertIdResolution &&
      resolveInsertIds !== undefined &&
      knownInsertIds !== undefined
    ) {
      knownInsertIds = new Set(resolveInsertIds());
    }
  };

  for (const unit of units) {
    if (unit.kind === "batch") {
      if (needsInsertIdResolution && resolveInsertIds !== undefined) {
        if (knownInsertIds === undefined) {
          knownInsertIds = new Set(resolveInsertIds());
        }
        dispatch(unit.batchCommand);
        const after = resolveInsertIds();
        const mintedIds = after.filter((id) => !knownInsertIds!.has(id));
        knownInsertIds = new Set(after);
        // All-or-nothing: `appendGeometryEntities` drops the WHOLE batch when
        // its shared spaceId does not exist, so a coalesced batch mints either
        // its full complement or none — fail-closed (push `undefined` slots)
        // on any OTHER observed count instead of assuming, the same defensive
        // posture the pre-T6b per-create loop takes for a partial mint.
        if (mintedIds.length === unit.originalCount) {
          for (const id of mintedIds) createdSlots.push(id);
        } else {
          for (let k = 0; k < unit.originalCount; k++) createdSlots.push(undefined);
        }
      } else {
        dispatch(unit.batchCommand);
      }
      continue;
    }

    const command = unit.command;

    if (
      needsInsertIdResolution &&
      resolveInsertIds !== undefined &&
      command.type === "geometry/create-block-insert"
    ) {
      if (knownInsertIds === undefined) {
        knownInsertIds = new Set(resolveInsertIds());
      }
      dispatch(command);
      const after = resolveInsertIds();
      const mintedIds = after.filter((id) => !knownInsertIds!.has(id));
      knownInsertIds = new Set(after);
      // Record the minted id OR `undefined` (a no-mint create still occupies a
      // slot so the group-size index below stays aligned to this block's run).
      // Exactly one new id is the expected case; anything else (0, the common
      // no-op-space case, or >1, an unexpected foreign mint) fails closed.
      createdSlots.push(mintedIds.length === 1 ? mintedIds[0] : undefined);
      continue;
    }

    if (
      command.type === "geometry/update-attribute-values" &&
      command.insertId === LAST_INSERT_ID_SENTINEL
    ) {
      // Bind to the FIRST freshly-created INSERT of THIS block's group (the
      // contracted target). The group is the LAST `groupSize` create slots; its
      // first copy is `createdSlots[length - groupSize]`. DROP the update —
      // fail-closed — unless this group ACTUALLY minted its full complement of
      // inserts: if it minted FEWER ids than expected (zero, or a partial run
      // because it targeted a non-existent space) there is no valid target, and
      // a cumulative success-only buffer would otherwise mis-bind the attrs onto
      // an EARLIER block's insert (overwriting that block when the tags match).
      // Also drops when no resolver is wired (the slots array is empty).
      const groupSize = readInsertGroupSize(command);
      const groupStart = createdSlots.length - groupSize;
      const groupSlots = groupStart >= 0 ? createdSlots.slice(groupStart) : [];
      const mintedInGroup = groupSlots.filter((id) => id !== undefined).length;
      const firstOfGroup = groupSlots[0];
      if (mintedInGroup === groupSize && firstOfGroup !== undefined) {
        // Strip the internal group-size hint before dispatch (contract-clean).
        const { [AI_INSERT_GROUP_SIZE_HINT]: _hint, ...rest } =
          command as Record<string, unknown>;
        dispatch({ ...rest, insertId: firstOfGroup } as EditorCommand);
        // T6b staleness fix — refresh the running baseline after this
        // dispatch too (see the STALENESS FIX paragraph on this function's
        // JSDoc). No-op when nothing was dispatched (the drop branch above).
        refreshKnownInsertIdsAfterNonCreatingDispatch();
      }
      continue;
    }

    dispatch(command);
    // T6b staleness fix — the generic fallthrough can ALSO mint a Block
    // entity as a side effect (e.g. a COPY targeting a pre-existing Block
    // insert), so refresh the running baseline here too, once tracking has
    // started, so a LATER creating unit's diff never sees a stale set.
    refreshKnownInsertIdsAfterNonCreatingDispatch();
  }

  // T2/T3 — AFTER-snapshot + net diff. CREATED = id present now but not in
  // `before` (mirrors `EntityMutation.added`); AFFECTED = id present in BOTH but
  // with a changed fingerprint (mirrors `EntityMutation.updated` — an in-place
  // modifier keeps the same id, so the fingerprint compare is what reveals it);
  // REMOVED = id present BEFORE but gone AFTER (T3 — mirrors `EntityMutation.removed`;
  // this is the ids the turn's ERASE actually deleted). Document order is preserved
  // (created/affected from the AFTER snapshot, removed from the BEFORE snapshot —
  // both already taken); every array is naturally deduped (each id appears once per
  // snapshot). The removed diff reuses the SAME single before/after snapshot the
  // T2 create/affect diff established — no extra document scan.
  if (beforeById === undefined || beforeOrdered === undefined) {
    return { createdEntityIds: [], affectedEntityIds: [], removedEntityIds: [] };
  }
  const createdEntityIds: string[] = [];
  const affectedEntityIds: string[] = [];
  const afterIds = new Set<string>();
  for (const after of snapshotEntities!()) {
    afterIds.add(after.id);
    const beforeFingerprint = beforeById.get(after.id);
    if (beforeFingerprint === undefined) {
      createdEntityIds.push(after.id);
    } else if (beforeFingerprint !== after.fingerprint) {
      affectedEntityIds.push(after.id);
    }
  }
  // REMOVED — walk the BEFORE rows (document order) and surface every id absent
  // from the AFTER set. A DELETE_ENTITIES of a SPACE entity lands here; a
  // DELETE_ENTITIES of a block-definition `attdef-*` id does NOT (the reducer
  // deletes only space entities, so the ATTDEF row is still present AFTER → it is
  // EXCLUDED from `removedEntityIds`, making the no-op NON-SILENT to the agent).
  const removedEntityIds: string[] = [];
  for (const before of beforeOrdered) {
    if (!afterIds.has(before.id)) removedEntityIds.push(before.id);
  }
  return { createdEntityIds, affectedEntityIds, removedEntityIds };
}

/**
 * Route a BATCH of AI-proposed commands through the safe contour.
 *
 * Fail-closed at TWO boundaries before anything is dispatched:
 *   1. SCHEMA PARSE — every proposal is parsed through the combined
 *      discriminated `aiCommandSchema` (`parseAiCommandBatch`), so schema
 *      defaults are applied, refines enforced, and a malformed / degenerate /
 *      unknown-action proposal is REJECTED (`AiCommandValidationError`) before
 *      any guard, translation, history batch, or dispatch.
 *   2. CC-2a aiCallable GUARD — translation runs `assertAiCallable*Action` for
 *      every action; a non-AI-callable target throws `NonAiCallableCommandError`
 *      still before any `dispatch`.
 *
 * Only once both pass is a single grouped-undo history batch opened. If a
 * dispatch throws mid-turn the batch is ABORTED (rolled back), never committed —
 * so a failed AI turn leaves the document unchanged.
 */
export function runAiCommandBatchThroughSafeContour(
  proposed: readonly AiCommand[],
  dispatch: AiSafeContourDispatch,
  options: RunAiThroughSafeContourOptions = {},
): AiSafeContourResult {
  // 1. SCHEMA PARSE (fail-closed). Defaults applied + refines enforced; a bad
  //    proposal throws AiCommandValidationError BEFORE any guard/dispatch.
  const parsed = parseAiCommandBatch(proposed);

  // 2. CC-2a VALIDATION + FAIL-CLOSED GUARD. Throws before any dispatch.
  const commands = translateAiCommandBatch(
    parsed,
    options.context ?? {},
    options.descriptors,
  );

  if (commands.length === 0) {
    // Nothing dispatched → no entity created, affected, or removed.
    return {
      commands,
      grouped: false,
      parsed,
      createdEntityIds: [],
      affectedEntityIds: [],
      removedEntityIds: [],
    };
  }

  // 3. GROUPED UNDO — one batch wraps the whole AI turn.
  const source = AI_COMMAND_SOURCE;
  const label = options.label ?? DEFAULT_AI_UNDO_LABEL;
  dispatch({ type: "history/begin-batch", source, label });
  let mutatedIds: {
    createdEntityIds: string[];
    affectedEntityIds: string[];
    removedEntityIds: string[];
  };
  try {
    // 4. DISPATCH → AUDIT → HISTORY through the real store pipeline (with
    //    LAST_INSERT_ID_SENTINEL substitution for INSERT_BLOCK attributes). The
    //    return surfaces the turn's CREATED / AFFECTED entity ids (T2) — a
    //    read-only diff of the mutations the dispatched commands already produced.
    mutatedIds = dispatchAiCommandSequence(
      commands,
      dispatch,
      options.resolveInsertIds,
      options.snapshotEntities,
    );
  } catch (error) {
    // 5. ABORT-BATCH-ON-THROW — roll back the partial AI turn instead of
    //    committing it. `history/abort-batch` reverts every accumulated
    //    mutation and discards the open batch; rethrow so the caller sees the
    //    failure (fail-closed: a failed AI turn leaves the document unchanged).
    dispatch({ type: "history/abort-batch", source });
    throw error;
  }
  // Commit ONLY on success.
  dispatch({ type: "history/commit-batch", source });

  return {
    commands,
    grouped: true,
    parsed,
    createdEntityIds: mutatedIds.createdEntityIds,
    affectedEntityIds: mutatedIds.affectedEntityIds,
    removedEntityIds: mutatedIds.removedEntityIds,
  };
}

/**
 * Route a SINGLE AI-proposed command through the safe contour. Thin wrapper
 * over {@link runAiCommandBatchThroughSafeContour} so a one-command AI turn is
 * still one grouped-undo unit.
 */
export function runAiCommandThroughSafeContour(
  proposed: AiCommand,
  dispatch: AiSafeContourDispatch,
  options: RunAiThroughSafeContourOptions = {},
): AiSafeContourResult {
  return runAiCommandBatchThroughSafeContour([proposed], dispatch, options);
}
